import { create } from "zustand"
import type { TViewerPane } from "services/annotations.service"

// Split-screen (side-by-side) state for the quotation editor — REQ-399.
//
// Lives in a store rather than in `useQuotationViewer` local state because the toggle button is
// rendered inside the Apryse shadow DOM by `applyViewerHeaderAndTools`, whose `render()` callback
// captures its props ONCE (Apryse owns the header lifecycle). A store subscription keeps the
// button's active state live without re-registering the header. `useSplitScreen` is the only
// writer.
type Store = {
	isSplitActive: boolean
	// True while the second viewer is entering/loading — the toggle is disabled to prevent a
	// double-enter (Apryse's enterMultiViewerMode is not re-entrant).
	isSplitPending: boolean
	// Whether entering split screen is possible right now (editor mode, viewer initialized, document
	// loaded, feature flag on). Lives here rather than as a header prop for the same reason as the
	// active flag — the header captures its props once, so a prop would never update.
	isSplitAvailable: boolean
	// Bumped every time entering/exiting split screen re-lays-out the viewer. Entering
	// MultiViewerMode makes Apryse re-mount the document area, so every DOM node captured earlier —
	// `documentViewer.getScrollViewElement()` above all — becomes DETACHED. Handlers bound to it
	// (contextmenu, mousemove, page-nav scroll, overlay ResizeObserver targets) silently stop
	// firing. Effects that touch those nodes list this epoch in their deps so they re-bind, and the
	// floating overlays re-position against the new half-width panes.
	layoutEpoch: number
	// "Auto open floor plan" — item 3 of the dual-screen control's menu (#1311). Gates the whole
	// smart-navigation state machine (decision D4): OFF is byte-for-byte the pre-#1311 behaviour,
	// where each pane's selection stays in its own pane.
	//
	// The INITIAL value here is false, but that is not the shipped default any more: #1383 turns it
	// ON in `useSplitScreen.enterSplit`, on every entry into dual screen, because a second pane whose
	// counterpart does not follow the selection is the less useful half of the feature. This stays
	// false so a viewer that never opens a second pane carries no smart-navigation state, and so the
	// teardown reset below has something to reset TO.
	//
	// In the store for the same reason as everything else here: the menu that owns it lives in the
	// custom header, whose Apryse `render()` callback captures its props once.
	isAutoOpenFloorPlan: boolean
	// Which pane's selection is currently driving the sidebars — the "Primary view" of the design's
	// Primary/Secondary rule, and the pane that carries the 2px active border. Always "primary" when
	// split screen is closed. Written by useSmartNavigation, read by the middle-dock host (which
	// publishes it to the stylesheet) and by useSmartNavigation itself.
	primaryPane: TViewerPane
	setSplitActive: (active: boolean) => void
	setSplitPending: (pending: boolean) => void
	setSplitAvailable: (available: boolean) => void
	bumpLayoutEpoch: () => void
	setAutoOpenFloorPlan: (enabled: boolean) => void
	toggleAutoOpenFloorPlan: () => void
	setPrimaryPane: (pane: TViewerPane) => void
}

export const useSplitScreenStore = create<Store>((set) => ({
	isSplitActive: false,
	isSplitPending: false,
	isSplitAvailable: false,
	layoutEpoch: 0,
	isAutoOpenFloorPlan: false,
	primaryPane: "primary",
	setSplitActive: (isSplitActive) => set({ isSplitActive }),
	setSplitPending: (isSplitPending) => set({ isSplitPending }),
	setSplitAvailable: (isSplitAvailable) => set({ isSplitAvailable }),
	bumpLayoutEpoch: () => set((state) => ({ layoutEpoch: state.layoutEpoch + 1 })),
	setAutoOpenFloorPlan: (isAutoOpenFloorPlan) => set({ isAutoOpenFloorPlan }),
	toggleAutoOpenFloorPlan: () => set((state) => ({ isAutoOpenFloorPlan: !state.isAutoOpenFloorPlan })),
	setPrimaryPane: (primaryPane) => set({ primaryPane }),
}))
